import React, { useEffect } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import {
  AnswerInput,
  HelperText,
  ProgramQuestionWithAnswer,
  useProgram,
} from 'shared/providers/ProgramProvider';
import {
  actQuestionId,
  beneficiaryTermQuestionId,
  learnQuestionId,
  serviceQuestionId,
  subServiceQuestionId,
} from 'shared/providers/IndicatorProvider/utils';
import { SERVICE_TYPE_QUESTION_ID } from 'npo/pages/ReportBuilder/Components/SectionMultiRadio';
import ExampleText from 'npo/pages/ReportBuilder/Components/ImpactSummary/ExampleText';
import PrimaryBeneficiaryField from './PrimaryBeneficiaryField';
import ServiceTypeField from './ServiceTypeField';
import CapacityDevelopmentField from './CapacityDevelopmentField';
import IntermediariesField from './IntermediariesField';
import {
  CAPACITY_DEVELOPMENT_QUESTION_ID,
  INTERMEDIARIES_QUESTION_ID,
} from 'shared/providers/ProgramProvider/utils';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import ImpactSummaryIntro from 'npo/pages/ReportBuilder/Components/ImpactSummary/ImpactSummaryIntro';
import { GuidanceTextResponse } from 'npo/pages/ReportBuilder/Components/utils';
import { BENEFICIARY_QUESTION_ID } from 'npo/pages/ReportBuilder/Components/Narrative/utils';
import BeneficiaryField from './BeneficiaryField';
import ProgramServiceCollectionField from './ProgramServiceCollectionField';
import ProgramSubServiceField from './ProgramSubServiceField';
import SelectorField from './SelectorField';
import BudgetField, { budgetQuestionIds } from './BudgetField';
import BudgetUploadField from './BudgetUploadField';
import BudgetSummaryField from './BudgetSummaryField';
import { BUDGET_FILE_QUESTION_ID } from 'npo/pages/ReportBuilder/Components/Budget/utils';
import LocationField from './LocationField';
import PeriodField, { PERIOD_QUESTION_ID } from './PeriodField';
import ReachIndicatorsField from './ReachIndicatorsField';
import { useBlockedSignal } from './useBlockedSignal';
import { REACH_INDICATORS_QUESTION_ID } from 'shared/providers/ProgramProvider/utils';
import { ReportsTable } from '../ReportGroup';
import {
  LengthCounter,
  MAX_CHARACTER_DESCRIPTION,
  TextArea,
} from 'npo/pages/ReportBuilder/Components/SectionLongText';

const BUDGET_SUMMARY_QUESTION_ID = 80011;
const LEARN_ACT_QUESTION_IDS = new Set([learnQuestionId, actQuestionId]);

const emptyGuidance: GuidanceTextResponse = {};
const noop = () => undefined;

const impactModelIntroQuestionId = 40000;
const socialImpactIndicatorsQuestionId = 40010;
const IMPACT_MODEL_INTRO_ANSWER = 'Continue';

const ImpactSummaryIntroField: React.FC<{
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
}> = ({ value, onChange }) => {
  // Seed a sentinel answer so isAnswerFilled() is true, letting
  // handleSaveAndAdvance persist the answer and mark the intro complete.
  // Depend on `value` (object identity) so we re-seed whenever
  // ProgramQuestionsModal's foundAnswer re-sync effect clobbers input back
  // to empty (which happens after this child's mount effect, since React
  // runs child effects before parent effects — a value.text-only dep would
  // never observe the clobber because text stays null across renders).
  useEffect(() => {
    if (value.text !== IMPACT_MODEL_INTRO_ANSWER) {
      onChange({ ...value, text: IMPACT_MODEL_INTRO_ANSWER });
    }
  }, [value]);
  return (
    <ImpactSummaryIntro
      hideBottomBar={true}
      guidanceText={emptyGuidance}
      handleNext={noop}
    />
  );
};

interface QuestionRendererProps {
  question: ProgramQuestionWithAnswer;
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  disabled?: boolean;
  report?: ReportsTable;
  onHideFooterChange?: (hide: boolean) => void;
  onBlockedChange?: (blocked: boolean) => void;
}

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.tiny};
  margin-bottom: ${SPACERS.small};

  input,
  textarea,
  select {
    font: inherit;
    padding: ${SPACERS.tiny} ${SPACERS.small};
    border: 1px solid ${COLORS.grayBase};
    border-radius: 4px;
    background-color: ${COLORS.whiteBase};
    &:focus {
      outline: none;
      border-color: ${COLORS.ctaPrimary};
    }
    &:disabled {
      background-color: ${COLORS.grayLightest};
      color: ${COLORS.greyDark};
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
  }

  .enum-option {
    display: flex;
    gap: ${SPACERS.tiny};
    align-items: center;
    padding: ${SPACERS.tiny};
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    &:hover {
      background-color: ${COLORS.grayLight};
    }
    input[type='radio'],
    input[type='checkbox'] {
      accent-color: ${COLORS.ctaPrimary};
    }
  }
`;

const LongTextWrapper = styled.div`
  display: block;
  width: 100%;
  .text-field {
    width: 100%;
    margin-bottom: ${SPACERS.tiny};
    background-color: ${COLORS.whiteBase} !important;
    textarea {
      border: none;
      padding: 0;
      background-color: transparent;
      &:focus {
        outline: none;
        border: none;
      }
    }
  }
`;

const normalizeType = (t: string | null | undefined): string =>
  (t ?? '').toLowerCase();

const isCheckboxType = (t: string) => t === 'checkboxes';

const isEnumType = (t: string) =>
  ['radio', 'enum', 'select', 'dropdown'].some(k => t.includes(k));

const parseCheckboxValue = (raw: string | null | undefined): string[] => {
  if (!raw) return [];
  const trimmed = raw.trim();
  if (!trimmed) return [];
  // Legacy answers were sometimes serialized with single quotes.
  const normalized = trimmed.replace(/'/g, '"');
  try {
    const parsed = JSON.parse(normalized);
    if (Array.isArray(parsed)) return parsed.map(String);
    if (typeof parsed === 'string') return [parsed];
  } catch {
    // Not JSON — fall through and treat as a single stored value.
  }
  return [trimmed];
};

interface CheckboxOption {
  key: React.Key;
  response: string;
}

interface CheckboxGroupProps {
  options: CheckboxOption[];
  value: string | null | undefined;
  disabled?: boolean;
  onChange: (nextText: string) => void;
}

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  value,
  disabled,
  onChange,
}) => {
  const selected = parseCheckboxValue(value);
  return (
    <SelectorField>
      <SelectorOptions
        options={options}
        variant="checkbox"
        margin="0"
        maxWidth="100%"
        disabled={disabled}
        getLabel={opt => opt.response}
        getSelected={opt => selected.includes(opt.response)}
        onClick={opt => {
          if (disabled) return;
          const isSelected = selected.includes(opt.response);
          const next = isSelected
            ? selected.filter(s => s !== opt.response)
            : [...selected, opt.response];
          onChange(next.length ? JSON.stringify(next) : '');
        }}
      />
    </SelectorField>
  );
};

const isDateType = (t: string) => t.includes('date');
const isRangeType = (t: string) =>
  t.includes('range') || t.includes('daterange');
const isNumberType = (t: string) => t.includes('number') || t.includes('int');
const isLongTextType = (t: string) =>
  t.includes('textarea') || t.includes('long') || t.includes('paragraph');

interface SocialImpactFieldProps {
  options: ReadonlyArray<{ id: string | number; name: string }>;
  value: string | null | undefined;
  disabled?: boolean;
  onChange: (nextText: string) => void;
  onBlockedChange?: (blocked: boolean) => void;
}

/**
 * Social-impact indicator checkbox group. Own component so the
 * "prerequisite service question unanswered" state can signal the
 * modal to disable Save & Continue via `useBlockedSignal` without
 * reshuffling QuestionRenderer's hook order.
 */
const SocialImpactField: React.FC<SocialImpactFieldProps> = ({
  options,
  value,
  disabled,
  onChange,
  onBlockedChange,
}) => {
  const checkboxOptions = options.map(opt => ({
    key: opt.id,
    response: opt.name,
  }));
  useBlockedSignal(checkboxOptions.length === 0, onBlockedChange);

  if (checkboxOptions.length === 0) {
    return (
      <Field>
        <TrueImpactText variant="body-small" color="critical">
          Please answer the previous service question before selecting social
          impact indicators.
        </TrueImpactText>
      </Field>
    );
  }

  return (
    <CheckboxGroup
      options={checkboxOptions}
      value={value}
      disabled={disabled}
      onChange={onChange}
    />
  );
};

const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  value,
  onChange,
  disabled,
  report,
  onHideFooterChange,
  onBlockedChange,
}) => {
  const { allSucceedIndicators } = useProgram();

  const update = (patch: Partial<AnswerInput>) => {
    onChange({ ...value, ...patch });
  };

  if (question.questionId === serviceQuestionId) {
    return (
      <ProgramServiceCollectionField
        sectionKey={question.section}
        questionId={question.questionId}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (question.questionId === subServiceQuestionId) {
    return (
      <ProgramSubServiceField
        sectionKey={question.section}
        questionId={question.questionId}
        value={value}
        onChange={onChange}
        disabled={disabled}
        onBlockedChange={onBlockedChange}
      />
    );
  }

  if (question.questionId === beneficiaryTermQuestionId) {
    return (
      <PrimaryBeneficiaryField
        sectionKey={question.section}
        questionId={question.questionId}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (question.questionId === SERVICE_TYPE_QUESTION_ID) {
    return (
      <ServiceTypeField
        sectionKey={question.section}
        questionId={question.questionId}
        enumOptions={question.enumResponses}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (question.questionId === CAPACITY_DEVELOPMENT_QUESTION_ID) {
    return (
      <CapacityDevelopmentField
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (question.questionId === INTERMEDIARIES_QUESTION_ID) {
    return (
      <IntermediariesField
        sectionKey={question.section}
        questionId={question.questionId}
        enumOptions={question.enumResponses}
        value={value}
        onChange={onChange}
        disabled={disabled}
        onBlockedChange={onBlockedChange}
      />
    );
  }

  if (question.questionId === BUDGET_FILE_QUESTION_ID && report) {
    return (
      <BudgetUploadField value={value} onChange={onChange} report={report} />
    );
  }

  if (question.questionId === BUDGET_SUMMARY_QUESTION_ID && report) {
    return (
      <BudgetSummaryField value={value} onChange={onChange} report={report} />
    );
  }

  if (budgetQuestionIds.has(question.questionId)) {
    return (
      <BudgetField
        questionId={question.questionId}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (question.questionId === impactModelIntroQuestionId) {
    return <ImpactSummaryIntroField value={value} onChange={onChange} />;
  }

  if (question.questionId === PERIOD_QUESTION_ID) {
    return (
      <PeriodField
        questionId={question.questionId}
        enumOptions={question.enumResponses}
        value={value}
        onChange={onChange}
        report={report}
      />
    );
  }

  if (question.questionId === BENEFICIARY_QUESTION_ID) {
    return (
      <BeneficiaryField
        value={value}
        onChange={onChange}
        disabled={disabled}
        onHideFooterChange={onHideFooterChange}
      />
    );
  }

  const type = normalizeType(question.questionType);

  if (type === 'address') {
    return (
      <LocationField value={value} onChange={onChange} disabled={disabled} />
    );
  }

  const handleCheckboxChange = (nextText: string) => update({ text: nextText });

  if (question.questionId === REACH_INDICATORS_QUESTION_ID) {
    if (!report) {
      return (
        <Field>
          <TrueImpactText variant="body-small" color="critical">
            Something went wrong while rendering this question. Please contact
            support.
          </TrueImpactText>
        </Field>
      );
    }
    return (
      <ReachIndicatorsField
        value={value}
        onChange={onChange}
        disabled={disabled}
        report={report}
        onBlockedChange={onBlockedChange}
      />
    );
  }

  if (question.questionId === socialImpactIndicatorsQuestionId) {
    return (
      <SocialImpactField
        options={allSucceedIndicators?.options ?? []}
        value={value.text}
        disabled={disabled}
        onChange={handleCheckboxChange}
        onBlockedChange={onBlockedChange}
      />
    );
  }

  const checkboxOptions = question.enumResponses.map(opt => ({
    key: opt.dbId,
    response: opt.response,
  }));
  const enumOptions = question.enumResponses;

  if (isCheckboxType(type) && checkboxOptions.length > 0) {
    return (
      <CheckboxGroup
        options={checkboxOptions}
        value={value.text}
        disabled={disabled}
        onChange={handleCheckboxChange}
      />
    );
  }

  if (isEnumType(type) && enumOptions.length > 0) {
    const answerIdFromEnum = question.answer?.enumResponse?.dbId ?? null;
    const selectedResponse =
      value.questionText ?? value.text ?? question.answer?.questionText ?? null;

    return (
      <>
        <HelperText
          sectionKey={question.section}
          questionId={question.questionId}
        />
        <SelectorField>
          <SelectorOptions
            options={enumOptions}
            variant="radio"
            margin="0"
            maxWidth="100%"
            disabled={disabled}
            getLabel={opt => opt.response}
            getSelected={opt =>
              value.answerId === opt.dbId ||
              (value.answerId == null &&
                (answerIdFromEnum === opt.dbId ||
                  (selectedResponse != null &&
                    selectedResponse === opt.response)))
            }
            onClick={opt => {
              if (disabled) return;
              update({
                answerId: opt.dbId,
                questionText: opt.response,
                // Learn / Act answers are read downstream as plain
                // 'Yes' / 'No' text (impact model stage toggles), so
                // persist the label like ReportBuilder does.
                ...(LEARN_ACT_QUESTION_IDS.has(question.questionId)
                  ? { text: opt.response }
                  : {}),
              });
            }}
          />
        </SelectorField>
        <ExampleText questionId={question.questionId} />
      </>
    );
  }

  if (isRangeType(type)) {
    return (
      <Field>
        <label>
          <TrueImpactText variant="body-small-bold">Start</TrueImpactText>
          <input
            type="date"
            disabled={disabled}
            value={value.date1 ?? ''}
            onChange={e => update({ date1: e.target.value || null })}
          />
        </label>
        <label>
          <TrueImpactText variant="body-small-bold">End</TrueImpactText>
          <input
            type="date"
            disabled={disabled}
            value={value.date2 ?? ''}
            onChange={e => update({ date2: e.target.value || null })}
          />
        </label>
      </Field>
    );
  }

  if (isDateType(type)) {
    return (
      <Field>
        <input
          type="date"
          disabled={disabled}
          value={value.date1 ?? ''}
          onChange={e => update({ date1: e.target.value || null })}
        />
      </Field>
    );
  }

  if (isNumberType(type)) {
    return (
      <Field>
        <input
          type="number"
          disabled={disabled}
          value={value.text ?? ''}
          onChange={e => update({ text: e.target.value })}
        />
      </Field>
    );
  }

  if (isLongTextType(type) || !type) {
    const text = value.text ?? '';
    return (
      <LongTextWrapper>
        <TextArea
          value={text}
          disabled={disabled}
          onChange={e => update({ text: e.target.value })}
          className="text-field"
          rows={undefined}
          minRows={4}
          maxRows={10}
          inputProps={{ maxLength: MAX_CHARACTER_DESCRIPTION }}
        />
        <LengthCounter length={text.length} />
      </LongTextWrapper>
    );
  }

  return (
    <Field>
      <input
        type="text"
        disabled={disabled}
        value={value.text ?? ''}
        onChange={e => update({ text: e.target.value })}
      />
    </Field>
  );
};

export default QuestionRenderer;
